import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, TrendingDown, AlertTriangle, ArrowDown, ArrowUp } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_LABELS = { high: "High", medium: "Medium", low: "Low" };
const PRIORITY_COLORS = { high: "text-red-600 bg-red-50", medium: "text-amber-600 bg-amber-50", low: "text-slate-600 bg-slate-50" };

function formatCurrency(val) {
  return new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", minimumFractionDigits: 0 }).format(val || 0);
}

export function DebtContent() {
  const { household } = useAuth();
  const [debts, setDebts] = useState([]);
  const [summary, setSummary] = useState({});
  const [strategy, setStrategy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", lender: "", balance: "", interest_rate: "", minimum_payment: "", planned_payment: "", due_date: "", priority: "medium", notes: "" });

  const fetchData = useCallback(async () => {
    if (!household?.household_id) { setLoading(false); return; }
    try {
      const [debtsRes, strategyRes] = await Promise.all([
        fetch(`${API_URL}/api/households/${household.household_id}/debts`, { credentials: "include" }),
        fetch(`${API_URL}/api/households/${household.household_id}/debts/strategy`, { credentials: "include" }),
      ]);
      if (!debtsRes.ok) {
        console.error(`[Debt] Fetch failed: ${debtsRes.status}`, await debtsRes.text().catch(() => ""));
        if (debtsRes.status === 401) toast.error("Session expired — please log in again.");
        else toast.error(`Could not load debts (${debtsRes.status}). Try refreshing.`);
        return;
      }
      const debtsData = await debtsRes.json();
      setDebts(debtsData.debts || []);
      setSummary(debtsData.summary || {});
      if (strategyRes.ok) {
        setStrategy(await strategyRes.json());
      }
    } catch (e) {
      console.error("[Debt] Network error:", e);
      toast.error("Could not reach the server. Check your connection and try again.");
    } finally { setLoading(false); }
  }, [household?.household_id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAdd = () => { setEditing(null); setForm({ name: "", lender: "", balance: "", interest_rate: "", minimum_payment: "", planned_payment: "", due_date: "", priority: "medium", notes: "" }); setDialogOpen(true); };
  const openEdit = (d) => { setEditing(d); setForm({ name: d.name, lender: d.lender || "", balance: String(d.balance), interest_rate: String(d.interest_rate || 0), minimum_payment: String(d.minimum_payment || 0), planned_payment: String(d.planned_payment || 0), due_date: d.due_date || "", priority: d.priority || "medium", notes: d.notes || "" }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.name.trim() || !form.balance) { toast.error("Name and balance are required."); return; }
    const body = { name: form.name.trim(), lender: form.lender.trim() || null, balance: parseFloat(form.balance), interest_rate: parseFloat(form.interest_rate) || 0, minimum_payment: parseFloat(form.minimum_payment) || 0, planned_payment: parseFloat(form.planned_payment) || 0, due_date: form.due_date || null, priority: form.priority, notes: form.notes.trim() || null };
    try {
      const url = editing ? `${API_URL}/api/households/${household.household_id}/debts/${editing.debt_id}` : `${API_URL}/api/households/${household.household_id}/debts`;
      const res = await fetch(url, { method: editing ? "PATCH" : "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(body) });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || "Save failed"); }
      toast.success(editing ? "Debt updated" : "Debt added");
      setDialogOpen(false); fetchData();
    } catch (e) { toast.error(e.message || "Could not save debt."); }
  };

  const handleDelete = async (d) => {
    if (!window.confirm(`Delete "${d.name}"? This cannot be undone.`)) return;
    try {
      await fetch(`${API_URL}/api/households/${household.household_id}/debts/${d.debt_id}`, { method: "DELETE", credentials: "include" });
      toast.success("Debt removed"); fetchData();
    } catch { toast.error("Could not delete debt."); }
  };

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading...</div>;

  return (
    <div className="p-6 max-w-4xl" data-testid="debt-content">
      {/* Summary strip */}
      {debts.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-card border rounded-lg p-4"><p className="text-xs text-muted-foreground mb-1">Total Debt</p><p className="text-lg font-semibold text-red-600" data-testid="total-debt">{formatCurrency(summary.total_balance)}</p></div>
          <div className="bg-card border rounded-lg p-4"><p className="text-xs text-muted-foreground mb-1">Minimum Payments</p><p className="text-lg font-semibold">{formatCurrency(summary.total_minimum_payment)}<span className="text-xs text-muted-foreground"> /mo</span></p></div>
          <div className="bg-card border rounded-lg p-4"><p className="text-xs text-muted-foreground mb-1">Planned Payments</p><p className="text-lg font-semibold text-emerald-600">{formatCurrency(summary.total_planned_payment)}<span className="text-xs text-muted-foreground"> /mo</span></p></div>
        </div>
      )}

      {/* Strategy guidance */}
      {strategy && debts.length > 1 && (
        <div className="mb-6 bg-amber-50/50 dark:bg-amber-950/10 border border-amber-200/50 dark:border-amber-900/30 rounded-lg p-4" data-testid="debt-strategy">
          <div className="flex items-center gap-2 mb-2"><TrendingDown className="w-4 h-4 text-amber-600" /><h3 className="text-sm font-semibold">Payoff Strategy</h3></div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-medium flex items-center gap-1"><ArrowDown className="w-3 h-3" /> Snowball <span className="text-xs text-muted-foreground">(smallest first)</span></p>
              <p className="text-xs text-muted-foreground mt-1">Pay off the smallest balance first for quick wins and motivation.</p>
              <ol className="mt-2 space-y-1">{strategy.snowball?.slice(0, 3).map((d, i) => <li key={i} className="text-xs text-muted-foreground">{i + 1}. {d.name} — {formatCurrency(d.balance)}</li>)}</ol>
            </div>
            <div>
              <p className="font-medium flex items-center gap-1"><ArrowUp className="w-3 h-3" /> Avalanche <span className="text-xs text-muted-foreground">(highest rate first)</span></p>
              <p className="text-xs text-muted-foreground mt-1">Pay off the highest interest rate first to save the most money.</p>
              <ol className="mt-2 space-y-1">{strategy.avalanche?.slice(0, 3).map((d, i) => <li key={i} className="text-xs text-muted-foreground">{i + 1}. {d.name} — {d.interest_rate}%</li>)}</ol>
            </div>
          </div>
          {strategy.recommendation && <p className="text-xs text-muted-foreground mt-3 pt-2 border-t">Suggested approach: <strong className="text-foreground capitalize">{strategy.recommendation}</strong></p>}
        </div>
      )}

      {/* Debt list */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold">Your Debts</h2>
        <Button onClick={openAdd} size="sm" variant="outline" className="rounded-full" data-testid="add-debt-btn"><Plus className="w-4 h-4 mr-1" />Add a debt</Button>
      </div>

      {debts.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed rounded-xl" data-testid="debt-empty">
          <AlertTriangle className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm font-medium mb-1">Add your debts to start building your payoff plan</p>
          <p className="text-xs text-muted-foreground mb-4">Track loans and repayments so you can see a clear path forward.</p>
          <Button onClick={openAdd} variant="outline" size="sm" data-testid="add-first-debt-btn">Add a debt</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {debts.map((d) => (
            <div key={d.debt_id} className="group bg-card border rounded-lg p-4 hover:border-amber-200 dark:hover:border-amber-800 transition-colors" data-testid={`debt-row-${d.debt_id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-sm">{d.name}</h3>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${PRIORITY_COLORS[d.priority] || PRIORITY_COLORS.medium}`}>{PRIORITY_LABELS[d.priority] || "Medium"}</span>
                  </div>
                  {d.lender && <p className="text-xs text-muted-foreground">{d.lender}</p>}
                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                    <span>Balance: <strong className="text-red-600">{formatCurrency(d.balance)}</strong></span>
                    <span>Rate: {d.interest_rate}%</span>
                    <span>Min: {formatCurrency(d.minimum_payment)}/mo</span>
                    {d.planned_payment > 0 && <span>Planned: <strong className="text-emerald-600">{formatCurrency(d.planned_payment)}/mo</strong></span>}
                    {d.payoff_months != null && <span>Payoff: <strong>{d.payoff_months} months</strong></span>}
                    {d.payoff_months === null && <span className="text-red-500">Payment too low to cover interest</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(d)} data-testid={`edit-debt-${d.debt_id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-600" onClick={() => handleDelete(d)} data-testid={`delete-debt-${d.debt_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" aria-describedby="debt-dialog-desc">
          <DialogHeader><DialogTitle>{editing ? "Edit Debt" : "Add Debt"}</DialogTitle></DialogHeader>
          <p id="debt-dialog-desc" className="sr-only">{editing ? "Edit debt details" : "Add a new debt"}</p>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-muted-foreground">Name *</label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Car Loan" data-testid="debt-name-input" /></div>
              <div><label className="text-xs font-medium text-muted-foreground">Lender</label><Input value={form.lender} onChange={e => setForm(p => ({ ...p, lender: e.target.value }))} placeholder="e.g., ANZ" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs font-medium text-muted-foreground">Balance *</label><Input type="number" step="0.01" value={form.balance} onChange={e => setForm(p => ({ ...p, balance: e.target.value }))} placeholder="0.00" data-testid="debt-balance-input" /></div>
              <div><label className="text-xs font-medium text-muted-foreground">Interest Rate %</label><Input type="number" step="0.01" value={form.interest_rate} onChange={e => setForm(p => ({ ...p, interest_rate: e.target.value }))} placeholder="0" /></div>
              <div><label className="text-xs font-medium text-muted-foreground">Priority</label>
                <Select value={form.priority} onValueChange={v => setForm(p => ({ ...p, priority: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent></Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs font-medium text-muted-foreground">Min Payment /mo</label><Input type="number" step="0.01" value={form.minimum_payment} onChange={e => setForm(p => ({ ...p, minimum_payment: e.target.value }))} placeholder="0" /></div>
              <div><label className="text-xs font-medium text-muted-foreground">Planned Payment /mo</label><Input type="number" step="0.01" value={form.planned_payment} onChange={e => setForm(p => ({ ...p, planned_payment: e.target.value }))} placeholder="0" /></div>
              <div><label className="text-xs font-medium text-muted-foreground">Due Date</label><Input type="date" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} /></div>
            </div>
            <div><label className="text-xs font-medium text-muted-foreground">Notes</label><Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional" /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button><Button onClick={handleSave} data-testid="save-debt-btn">{editing ? "Save Changes" : "Add Debt"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
